// Prior-Period Restatement · Phase 3b — the restatement on the screens that are not the statements: the lead sheet's
// "As previously reported" block (D-6), A9003's derived "Restated" rows (D-7), the client-data note (Analytics / Scoping),
// the Final Analytical Review's "as previously reported" figure, W9's prior-period section and the W10 / TCWG list.
// Every block renders NOTHING when the payload carries no restatement (no posted entry) — the screens stay as they were.
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import Amount from "@/components/Amount";
import { asArray } from "@/lib/asArray";

const box = "rounded border border-sky-300 bg-sky-50 p-2 text-[11px] text-sky-950 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-100";

export function RestatementLeadSheetBlock({ data }) {
  const colW = useResizableColumns({ storageKey: "restatement-lead-sheet-block", defaults: [110, 130, 130, 130, 160], mins: [72, 90, 90, 90, 100] });   // Step 11d · add-on
  const { t } = useI18n();
  const rows = asArray(data?.rows).filter((r) => r.restated);
  if (!data?.restated || !rows.length) return null;
  return (
    <div className={box} data-testid="lead-restatement">
      <div className="mb-1 font-semibold">{t("pya3b.leadTitle")}</div>
      <ResizableTable cols={colW} wrap className="w-full">
        <thead><tr>
          <ResizableTh index={0} className="px-1 text-start">{t("lead.col.account")}</ResizableTh>
          <ResizableTh index={1} className="px-1 text-end">{t("pyaFs.asPreviouslyReported")}</ResizableTh>
          <ResizableTh index={2} className="px-1 text-end">{t("pyaFs.effect")}</ResizableTh>
          <ResizableTh index={3} className="px-1 text-end">{t("pya3b.restatedPy")}</ResizableTh>
          <ResizableTh index={4} className="px-1 text-start">{t("pya3b.entries")}</ResizableTh>
        </tr></thead>
        <tbody>
          {rows.map((r) => {
            const applied = r.py_source !== "none" && !r.py_manual;
            return (
              <tr key={r.raw_account_code} data-testid={`lead-restatement-row-${r.raw_account_code}`}>
                <td className="px-1 font-mono">{r.raw_account_code}</td>
                <td className="px-1 text-end"><Amount value={applied ? r.py_as_previously_reported : r.py_final} /></td>
                <td className="px-1 text-end"><Amount value={r.py_restatement} /></td>
                <td className="px-1 text-end">{applied ? <Amount value={r.py_final} /> : <span data-testid={`lead-restatement-own-${r.raw_account_code}`}>{t("pya3b.ownAmount")}</span>}</td>
                <td className="px-1">{asArray(r.py_restatement_refs).join("; ")}</td>
              </tr>
            );
          })}
          <tr className="border-t font-semibold" data-testid="lead-restatement-total">
            <td className="px-1">{t("lead.totals")}</td>
            <td className="px-1 text-end"><Amount value={data.total_py_as_previously_reported} /></td>
            <td />
            <td className="px-1 text-end"><Amount value={data.totals?.py_final} /></td>
            <td />
          </tr>
        </tbody>
      </ResizableTable>
    </div>
  );
}

export function RestatementA9003Block({ data }) {
  const colW2 = useResizableColumns({ storageKey: "restatement-a9003-block", defaults: [110, 130, 130, 130, 130, 130, 160], mins: [72, 90, 90, 90, 90, 90, 100] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const rows = asArray(data?.rows).filter((r) => r.restated_derived || r.cy_opening_restatement);
  if (!data?.restatement || !rows.length) return null;
  return (
    <div className={box} data-testid="ob-restatement">
      <div className="mb-1 font-semibold">{t("pya3b.obTitle")}</div>
      <div className="mb-1">{t("pya3b.obRule")}</div>
      <ResizableTable cols={colW2} wrap className="w-full">
        <thead><tr>
          <ResizableTh index={0} className="px-1 text-start">{t("lead.col.account")}</ResizableTh>
          <ResizableTh index={1} className="px-1 text-end">{t("pyaFs.asPreviouslyReported")}</ResizableTh>
          <ResizableTh index={2} className="px-1 text-end">{t("pyaFs.effect")}</ResizableTh>
          <ResizableTh index={3} className="px-1 text-end">{t("pya3b.restatedPy")}</ResizableTh>
          <ResizableTh index={4} className="px-1 text-end">{t("pya3b.cyOpeningAdj")}</ResizableTh>
          <ResizableTh index={5} className="px-1 text-end">{t("pya3b.difference")}</ResizableTh>
          <ResizableTh index={6} className="px-1 text-start">{t("pya3b.restatedFlag")}</ResizableTh>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.account_code} data-testid={`ob-restatement-row-${r.account_code}`}>
              <td className="px-1 font-mono">{r.account_code}</td>
              <td className="px-1 text-end"><Amount value={r.py_closing_as_previously_reported} /></td>
              <td className="px-1 text-end"><Amount value={r.py_restatement} /></td>
              <td className="px-1 text-end"><Amount value={r.py_closing} /></td>
              <td className="px-1 text-end">{r.cy_opening_restatement != null ? <Amount value={r.cy_opening_restatement} /> : "—"}
                {r.cy_opening_restatement_reason && (   /* STEP 11b-2 · F6: why the restatement moves the CY opening */
                  <div className="text-start text-[10px] text-muted-foreground" data-testid={`ob-restatement-why-${r.account_code}`}>
                    {lang === "ar" ? (r.cy_opening_restatement_reason_ar || r.cy_opening_restatement_reason) : r.cy_opening_restatement_reason}</div>)}</td>
              <td className="px-1 text-end">{r.difference != null ? <Amount value={r.difference} /> : "—"}</td>
              <td className="px-1" data-testid={`ob-restated-flag-${r.account_code}`}>
                {r.restated_derived ? `${t("pya3b.restatedYes")} — ${r.restated_reason || ""}` : "—"}
                {r.restated_typed ? <span className="ms-1 text-muted-foreground">({t("pya3b.typedKept")})</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
    </div>
  );
}

// Analytics / Scoping (client data, B32-1): one line when a restatement entry is posted
export function RestatementClientDataNote({ engId, screen }) {
  const { t } = useI18n();
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!engId) return;
    api.get(`/engagements/${engId}/restatements`, { _silent: true })
      .then((r) => setOn(asArray(r.data?.entries).some((e) => e.status === "posted")))
      .catch(() => setOn(false));
  }, [engId]);
  if (!on) return null;
  return (
    <div className={box} data-testid={`restatement-client-note-${screen}`}>
      {screen === "analytics" ? t("pya3b.analyticsNote") : t("pya3b.scopingNote")}
    </div>
  );
}

export function RestatementList({ items, testid }) {
  const { t } = useI18n();
  const list = asArray(items);
  if (!list.length) return null;
  return (
    <div className={box} data-testid={testid}>
      <div className="mb-1 font-semibold">{t("pya3b.listTitle")}</div>
      <ul className="list-disc ps-5">
        {list.map((e) => (
          <li key={e.ref} data-testid={`${testid}-${e.ref}`}>
            <span className="font-mono">{e.ref}</span> · {t(`pya.type.${e.type}`)} · {e.description}
            {" · "}{t("pya3b.profitEffect")}: <Amount value={e.profit_effect} />
            {/* PR #52 SF-7: by the FS mapping; the OCI effect on its own, only when there is one */}
            {!!Number(e.oci_effect) && <span data-testid={`${testid}-${e.ref}-oci`}>{" · "}{t("pya3b.ociEffect")}: <Amount value={e.oci_effect} /></span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RestatementW9Section({ state }) {
  const { t, lang } = useI18n();
  const rows = asArray(state?.rows_prior_period);
  if (!rows.length) return null;
  return (
    <div className={box} data-testid="w9-prior-period">
      <div className="mb-1 font-semibold">{t("pya3b.w9Title")}</div>
      <div className="mb-1">{lang === "ar" ? state.prior_period_rule_ar : state.prior_period_rule_en}</div>
      <ul className="list-disc ps-5">
        {rows.map((r) => (
          <li key={r.id} data-testid={`w9-prior-period-row-${r.id}`}>
            {r.description} · <Amount value={r.amount} /> · {t("pya3b.restatedByEntry")} {r.restated_by?.ref}
          </li>
        ))}
      </ul>
    </div>
  );
}

// FAR: the prior year as previously reported under the restated figure (lines, subtotals, ratios)
export function AprBeneath({ row, fmt }) {
  const { t } = useI18n();
  if (!row || row.py_as_previously_reported === undefined) return null;
  const v = row.py_as_previously_reported;
  return (
    <div className="text-[9px] text-sky-800 dark:text-sky-300" data-testid={`far-apr-${row.key}`}>
      {t("pyaFs.asPreviouslyReported")}: {v == null ? "—" : (fmt ? fmt(v) : <Amount value={v} />)}
    </div>
  );
}
